import React, { FC, useMemo, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import {
  AddNewReportFormCont,
  FormCellCont,
  FormElement,
  FormFieldsCont,
  Label,
} from './AddNewReportForm.styles';
import { IPostReportingGroupBody, IReportingGroupMappingOption } from '../../../services/reports';
import { IReportsSetupItem } from '../../../services/surveys';
import SearchDropdown, { ISearchDropdownItem } from '../../SearchDropdown/SearchDropdown';
import SearchInputHeader from '../../SearchDropdown/SearchInputHeader/SearchInputHeader';

const NO_MAPPING_ID = '';

interface IAddNewReportForm {
  submit: (data: IPostReportingGroupBody) => void;
  apiError?: { [key: string]: string };
  editData?: IReportsSetupItem;
  isEffectSurvey: boolean;
  mappingOptions?: IReportingGroupMappingOption[];
}

const AddNewReportForm: FC<IAddNewReportForm> = ({
  submit,
  apiError,
  editData,
  isEffectSurvey,
  mappingOptions = [],
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const typeOptions = useMemo(
    () => [
      {
        id: '1',
        name: t('viewSurvey.reportsSetup.teamReports'),
        icon: '',
        shortDisplayName: 'team',
      },
      {
        id: '2',
        name: t('viewSurvey.reportsSetup.executiveReports'),
        icon: '',
        shortDisplayName: 'executive',
      },
    ],
    [t]
  );

  const hasMappingOptions = mappingOptions.length > 0;

  const mappedDepartmentOptions = useMemo<ISearchDropdownItem[]>(
    () => [
      { id: NO_MAPPING_ID, name: t('viewSurvey.reportsSetup.modals.mappedDepartment.noMapping') },
      ...mappingOptions.map(({ id, name }) => ({ id, name })),
    ],
    [mappingOptions, t]
  );

  const addReportSchema = object({
    name: string()
      .required(t('viewSurvey.reportsSetup.modals.validations.nameIsRequired'))
      .matches(
        /^[^&$@=;/:+,?+\\{}^%`[\]~"'<>#|]*$/,
        t('viewSurvey.reportsSetup.modals.validations.nameForbidenChars')
      ),
    type: object().nullable(),
    mappedDepartment: object().nullable(),
  });

  const defaultValues = {
    name: editData?.name || '',
    type: editData?.type
      ? typeOptions.find(({ shortDisplayName }) => shortDisplayName === editData.type)
      : typeOptions[0],
    mappedDepartment: editData?.mappedDepartment?.id
      ? { id: editData.mappedDepartment.id, name: editData.mappedDepartment.name || '' }
      : null,
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    // ! TODO: have to be resolved later
    // @ts-expect-error task-21: @hookform/resolvers@5 is typed for yup 1, the app is on yup 0.32
    resolver: yupResolver(addReportSchema),
  });

  const saveForm = ({ type, mappedDepartment, ...rest }: typeof defaultValues) => {
    submit({
      ...rest,
      type: type?.shortDisplayName || '',
      mappedDepartment: hasMappingOptions
        ? mappedDepartment?.id || null
        : editData?.mappedDepartment?.id || null,
    });
  };

  const mappedDepartmentField = (
    <FormElement id="MappedDepartmentFormElement">
      <Label id="Label">{t('viewSurvey.reportsSetup.mappedDepartment')}</Label>
      <FormCellCont id="FormCellCont">
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <SearchDropdown
              items={mappedDepartmentOptions}
              placeholder={t('viewSurvey.reportsSetup.modals.mappedDepartment.placeholder')}
              selectEl={el => onChange(typeof el === 'object' && el.id ? el : null)}
              width="320px"
              activeEl={value ?? undefined}
              error={apiError?.mappedDepartment}
              selectFullObject
            />
          )}
          name="mappedDepartment"
        />
      </FormCellCont>
    </FormElement>
  );

  return (
    <AddNewReportFormCont
      id="AddNewReportFormCont"
      onSubmit={handleSubmit(saveForm)}
      isEffectSurvey={isEffectSurvey}
    >
      {isEffectSurvey ? null : (
        <FormFieldsCont id="TypeAndMappedDepartmentCont">
          <FormElement id="FormElement">
            <Label id="label">{t('reports.type')}</Label>
            <FormCellCont id="FormCellCont">
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SearchDropdown
                    items={typeOptions}
                    placeholder="type"
                    selectEl={onChange}
                    width="320px"
                    activeEl={value}
                    error={errors?.type?.message || apiError?.type}
                    selectFullObject
                    isDisabled={!!editData}
                  />
                )}
                name="type"
              />
            </FormCellCont>
          </FormElement>

          {hasMappingOptions ? mappedDepartmentField : null}
        </FormFieldsCont>
      )}

      <FormFieldsCont id="NameAndMappedDepartmentCont">
        <FormElement id="FormElement">
          <Label id="Label">{t('reports.name')}</Label>
          <FormCellCont id="FormCellCont">
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchInputHeader
                  width="320px"
                  noDrop
                  searchForList={onChange}
                  active={false}
                  placeholder={t('reports.name')}
                  inputRef={inputRef}
                  defaultValue={value}
                  error={errors?.name?.message || apiError?.name}
                />
              )}
              name="name"
            />
          </FormCellCont>
        </FormElement>

        {isEffectSurvey && hasMappingOptions ? mappedDepartmentField : null}
      </FormFieldsCont>
      <button data-testid="submitAddReport" id="AddReportModalCallToAction" type="submit" hidden>
        callToAction
      </button>
    </AddNewReportFormCont>
  );
};

export default AddNewReportForm;
